import { zodResolver } from '@hookform/resolvers/zod';
import {
  DocusignConfig,
  DocusignFormSchema,
  DocusignForm as DocusignFormType,
  ESignAccountBaseUrlEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../../../api/axios';
import { useEsignMutation } from '../../../../../api/settingsService';
import {
  MAlert,
  MBox,
  MButton,
  MDivider,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MStack,
} from '../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { ESIGN } from '../../../../../constants';
import { useAuth } from '../../../../../services/auth0';
import { useToast } from '../../../../../services/toast';
import { useEsignConsentHandler } from './useDocusignConsentHandler';

export function getDefaultDocusignEnvironment(
  accountBaseURI: string | undefined | null,
  isTestTenant: boolean | undefined | null,
): ESignAccountBaseUrlEnum {
  if (
    accountBaseURI &&
    (Object.values(ESignAccountBaseUrlEnum) as string[]).includes(
      accountBaseURI,
    )
  ) {
    return accountBaseURI as ESignAccountBaseUrlEnum;
  }

  // If no accountBaseURI, fallback to testTenant
  if (isTestTenant) {
    return ESignAccountBaseUrlEnum.DEMO;
  }

  return ESignAccountBaseUrlEnum.PRODUCTION;
}

function getEnvironmentWarning(
  isTestTenant: boolean,
  environment: ESignAccountBaseUrlEnum,
) {
  if (
    isTestTenant &&
    (environment === ESignAccountBaseUrlEnum.PRODUCTION ||
      environment === ESignAccountBaseUrlEnum.PRODUCTION_EU)
  ) {
    return {
      message:
        'Warning: You are using a production Docusign environment with a test tenant. You should use a Docusign developer sandbox for testing.',
    };
  }
  if (!isTestTenant && environment === ESignAccountBaseUrlEnum.DEMO) {
    return {
      message:
        'Warning: You are using a demo Docusign environment with a production tenant. You should use sandboxes for testing.',
    };
  }
}

interface DocusignFormProps {
  esign: Maybe<DocusignConfig>;
  onClose: () => void;
}

const DocusignForm = ({ esign, onClose }: DocusignFormProps) => {
  const { currentTenant } = useAuth();
  const { addToast } = useToast();

  /**
   * Handle saving esign settings
   * If the user requires consent, then we open a window for them to authorize and consent
   * IF the window fails to open (e.g. popups blocked) then there is a fallback link the user can click
   */
  const { mutate: doUpsert, isPending: isSaving } = useEsignMutation({
    retry: false,
    onSuccess: (_, variables) => {
      addToast({
        severity: 'success',
        summary:
          variables.method === 'update'
            ? 'Docusign Configuration Updated'
            : 'Docusign Configuration Created',
      });
      onClose();
    },
    onError: (error, payload) => {
      if (
        payload.method !== 'delete' &&
        error instanceof AxiosError &&
        (error.response?.data?.message as string)?.includes('consent_required')
      ) {
        // If the user has not consented, take them through the consent flow (opens new window)
        handleConsent(payload);
      } else {
        handleApiErrorToast(error);
      }
    },
  });

  const { handleConsent, isAwaitingConsent } = useEsignConsentHandler({
    onConsent: async (success, params) => {
      if (!success) {
        handleApiErrorToast(
          new Error(
            'There was an error during the consent process, please try again.',
          ),
        );
        return;
      }
      doUpsert(params);
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    watch,
    getValues,
  } = useForm<DocusignFormType>({
    resolver: zodResolver(DocusignFormSchema),
    mode: 'onChange',
    defaultValues: {
      provider: 'DOCUSIGN',
      accountBaseURI: getDefaultDocusignEnvironment(
        esign?.accountBaseURI,
        currentTenant?.testTenant,
      ),
      userId: esign?.userId || '',
      hmacKey: esign?.hmacKey || '',
      accountId: esign?.accountId || '',
    },
  });

  const watchEnvironment = watch('accountBaseURI');

  const onSubmit = async (data: DocusignConfig) => {
    doUpsert({
      method: esign ? 'update' : 'create',
      data,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <MGrid templateColumns="repeat(10, 1fr)" rowGap="3.5" columnGap="4">
        <MGridItem colSpan={5}>
          <MFormField
            error={errors.userId}
            label="User ID"
            isRequired
            tooltip={`Find this under the "Apps and Keys" in your Docusign settings.`}
          >
            <Controller
              name="userId"
              control={control}
              render={({ field }) => <MInput {...field} />}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={5}>
          <MFormField
            error={
              errors.accountBaseURI ||
              getEnvironmentWarning(
                !!currentTenant?.testTenant,
                watchEnvironment as ESignAccountBaseUrlEnum,
              )
            }
            label="Environment"
            isRequired
          >
            <Controller
              name="accountBaseURI"
              control={control}
              render={({ field }) => (
                <MCustomSelect
                  items={
                    currentTenant?.testTenant
                      ? ESIGN.DocusignAccountUrlDisplay
                      : ESIGN.DocusignAccountUrlDisplayForProduction
                  }
                  key="value"
                  {...field}
                />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={10}>
          <MFormField
            error={errors.hmacKey}
            label="HMAC Key / Secret Connect Key"
            isRequired
            tooltip={`Create this under "Connect > Connect Keys" in your Docusign settings.`}
          >
            <Controller
              name="hmacKey"
              control={control}
              render={({ field }) => <MInput {...field} />}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={10}>
          <MFormField
            error={errors.accountId}
            label="API Account ID"
            tooltip={
              <MStack>
                <MBox>
                  If you have multiple accounts, you can specify the account ID
                  here. Otherwise, leave it blank to use your default account.
                </MBox>
                <MBox fontWeight={500} mt={2}>
                  Find this under the "Apps and Keys" in your Docusign settings.
                </MBox>
              </MStack>
            }
          >
            <Controller
              name="accountId"
              control={control}
              render={({ field }) => (
                <MInput {...field} value={field.value || ''} />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={10}>
          <MDivider my={1} />
        </MGridItem>
        {isAwaitingConsent && (
          <MGridItem colSpan={10}>
            <MAlert type="info">
              Waiting for Docusign authorization. If the window did not open,{' '}
              <MButton
                variant="link"
                size="xs"
                onClick={() => {
                  handleConsent({
                    method: esign ? 'update' : 'create',
                    data: getValues(),
                  });
                }}
              >
                click here
              </MButton>{' '}
              to authorize.
            </MAlert>
          </MGridItem>
        )}
        <MGridItem colSpan={10}>
          <MFlex justify="end" gap={4}>
            <MButton
              disabled={isAwaitingConsent}
              variant="cancel"
              onClick={() => onClose()}
            >
              Cancel
            </MButton>

            <MButton
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              isDisabled={!isDirty || !isValid || isAwaitingConsent}
              isLoading={isSaving || isAwaitingConsent}
            >
              Connect
            </MButton>
          </MFlex>
        </MGridItem>
      </MGrid>
    </form>
  );
};

export default DocusignForm;
